import {
  AlarmClock,
  Bell,
  Bot,
  CalendarClock,
  MessageSquare,
  UserPlus,
} from 'lucide-react';

import { dashboardLink } from 'core/utils/dashboardDeepLink';

/**
 * Карта видов уведомлений: иконка, тон и куда ведёт клик (ТЗ 0068).
 *
 * Модуль чистый — без React, поэтому проверяется отдельным unit-тестом. Виды
 * приходят с сервера строками (реестр там, и новый вид не стоит миграции),
 * поэтому неизвестный ключ обязан иметь разумный вид по умолчанию, а не
 * ронять ленту.
 */
export type TNotificationTone = 'accent' | 'warn' | 'danger';

export interface INotificationKindMeta {
  icon: typeof Bell;
  tone: TNotificationTone;
  /** Ключ i18n для заголовка строки. */
  labelKey: string;
  /** Ссылка на задачу из уведомления. */
  href: (taskId: string) => string;
}

/**
 * Ссылка на задачу из уведомления.
 *
 * 🔴 `taskMode` НЕ всегда `'mine'`. Инвариант «каждое уведомление — про задачу,
 * назначенную мне» оказался неверным: адресаты `task_commented` — это ещё и
 * автор задачи, и все прошлые комментаторы, для которых задача не «моя».
 * Ссылка с `mine` уводила их в серверную выборку `assigneeMe: true`, где
 * задачи нет, и человек вместо карточки получал тост «Задача не найдена».
 *
 * `withClosed: true` остаётся всегда: уведомление живёт дольше, чем задача
 * остаётся открытой.
 */
const taskHref = (
  taskFilter: 'all' | 'today' | 'overdue',
  extra: Record<string, unknown> = {},
) => (taskId: string): string =>
  dashboardLink({
    view: 'tasks',
    taskMode: 'mine',
    taskFilter,
    withClosed: true,
    task: taskId,
    ...extra,
  });

/**
 * Ссылка для видов, где адресат может НЕ быть исполнителем: команда вместо
 * личного списка и никаких сужающих фильтров.
 */
const teamTaskHref = (taskId: string): string =>
  dashboardLink({
    view: 'tasks',
    taskMode: 'team',
    taskFilter: 'all',
    withClosed: true,
    task: taskId,
  });

export const NOTIFICATION_KINDS: Record<string, INotificationKindMeta> = {
  task_assigned: {
    icon: UserPlus,
    tone: 'accent',
    labelKey: 'notifications.kind.taskAssigned',
    href: taskHref('all'),
  },
  task_due_today: {
    icon: CalendarClock,
    tone: 'warn',
    labelKey: 'notifications.kind.taskDueToday',
    href: taskHref('today'),
  },
  task_overdue: {
    icon: AlarmClock,
    tone: 'danger',
    labelKey: 'notifications.kind.taskOverdue',
    href: taskHref('overdue'),
  },
  task_high_priority: {
    icon: AlarmClock,
    tone: 'danger',
    // Вид сохранён ради ленты (ТЗ 0109), но приоритета больше нет: это
    // понедельничная СВОДКА «открытые задачи без срока или со сроком позже»,
    // а не одна задача. Ведём в МОЙ СПИСОК без открытой карточки: id в
    // уведомлении сводки ни на что не указывает. Чип «без срока» не ставим —
    // сводка считает и задачи со сроком позже, он отсёк бы их; в списке
    // «без срока» и так своя группа.
    labelKey: 'notifications.kind.taskHighPriority',
    href: () =>
      dashboardLink({
        view: 'tasks',
        taskMode: 'mine',
        taskView: 'list',
        taskFilter: 'all',
      }),
  },
  task_auto_created: {
    icon: Bot,
    tone: 'accent',
    labelKey: 'notifications.kind.taskFromBot',
    href: taskHref('all'),
  },
  task_commented: {
    icon: MessageSquare,
    tone: 'accent',
    labelKey: 'notifications.kind.taskCommented',
    // Командный режим: адресат — исполнитель, автор задачи ИЛИ прошлый
    // комментатор, и двое последних в «моих задачах» её не найдут.
    href: teamTaskHref,
  },
};

const FALLBACK: INotificationKindMeta = {
  icon: Bell,
  tone: 'accent',
  labelKey: 'notifications.kind.unknown',
  href: taskHref('all'),
};

/** Метаданные вида. Неизвестный ключ не роняет ленту — он показывается
 *  нейтрально и всё равно ведёт на свою задачу. */
export const kindMeta = (kind: string): INotificationKindMeta =>
  NOTIFICATION_KINDS[kind] ?? FALLBACK;
